import React, { FC, useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import i18n from 'i18next';
import { getFirstDisplayText, getKeyByLang, getNameByLang } from "utils/util";
import { MinusIcon, PlusIcon } from "@heroicons/react/solid";
import { CartCacheLocalStorageKey } from "utils/constant";
import { trackAddToCart, trackRemoveFromCart } from "utils/analytics";


export interface ComponentProps {
    className?: string;
    cartSkuList: any[];
    onCartSkuListChange: () => void;
};

const CartSkuItems: FC<ComponentProps> = ({ className = "", cartSkuList, onCartSkuListChange }) => {
    const { t } = useTranslation();
    const { language } = i18n;

    const handleChangeCountButtonClick = (type: 'decrease' | 'increase', sku: any, info: any) => {
        const index = cartSkuList.findIndex(item => item.productId === info.productId);
        if (index < 0) {
            return;
        }
        const skuIndex = cartSkuList[index].skuList.findIndex((item: any) => item.id === sku.id);
        if (skuIndex < 0) {
            return;
        }
        const currentQuantity = Number(cartSkuList[index].skuList[skuIndex].quantity) || 0;
        const stocks = Number(sku?.stocks ?? sku?.stock);
        const hasStockLimit = Number.isFinite(stocks) && stocks >= 0;
        let quantity = currentQuantity;
        if (type === 'increase') {
            if (hasStockLimit && currentQuantity >= stocks) {
                return;
            }
            quantity = currentQuantity + 1;
        } else if (currentQuantity <= 0) {
            return;
        } else {
            quantity = currentQuantity - 1;
        }
        const nextCartSkuList = cartSkuList.map((item, itemIndex) => {
            if (itemIndex !== index) {
                return item;
            }
            return {
                ...item,
                skuList: item.skuList.map((skuItem: any, currentSkuIndex: number) => (
                    currentSkuIndex === skuIndex ? { ...skuItem, quantity } : skuItem
                )),
            };
        });
        window.localStorage.setItem(CartCacheLocalStorageKey, JSON.stringify(nextCartSkuList));
        const productName = getFirstDisplayText(info?.[getKeyByLang(language, 'productName')], info?.productName, info?.title, info?.name);
        const skuName = getFirstDisplayText(sku?.[getNameByLang(language)], sku?.name, sku?.nameHk, sku?.nameEn, productName);
        const changedItem = {
            item_id: String(sku.id),
            item_name: productName || String(info.productId || sku.id),
            item_variant: skuName || undefined,
            price: Number(sku?.sellingPrice) || 0,
            quantity: 1,
        };
        if (type === "increase") {
            trackAddToCart([changedItem]);
        } else {
            trackRemoveFromCart([changedItem]);
        }
        onCartSkuListChange();
    }

    return (
        <>
            {cartSkuList.map((item) => {
                const productName = getFirstDisplayText(item?.[getKeyByLang(language, 'productName')], item?.productName, item?.title, item?.name);
                const formattedTitle = productName.replaceAll(";", "");
                return (
                    <div
                        className="nc-NcInputNumber w-full listingSection__wrap"
                        data-nc-id="NcInputNumber"
                        key={item.productId}
                        style={{ padding: "12px" }}
                    >
                        <span className="font-medium text-neutral-800 dark:text-neutral-200">{formattedTitle}</span>
                        {item.skuList.map((sku: any) => {
                            const skuName = getFirstDisplayText(sku?.[getNameByLang(language)], sku?.name, sku?.nameHk, sku?.nameEn, productName);
                            const stocks = Number(sku?.stocks ?? sku?.stock);
                            const reachedStockLimit = Number.isFinite(stocks) && stocks >= 0 && (Number(sku.quantity) || 0) >= stocks;
                            const reachedMinQuantity = (Number(sku.quantity) || 0) <= 0;
                            return (
                                <div className="flex" style={{ margin: "10px 0" }} key={sku.id}>
                                    <div style={{ width: "80%", marginLeft: "12px" }}>
                                        <div className="flex">
                                            <img src={sku?.pictureUrl || ''} alt={skuName} className="w-[80px] h-[80px] shrink-0 mr-[12px]" />
                                            <div className="">
                                                <div className="text-[16px]">{skuName}</div>
                                                <div className="text-[16px] font-semibold mt-[4px]">{t('cart.price')}：{`HKD $${sku?.sellingPrice || ''}`}</div>
                                                <div className="text-[16px] font-semibold mt-[4px]">{t('cart.total_amount')}：{`HKD $${sku.originTotalPrice}`}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={`flex items-center justify-between`} style={{ width: "11rem" }}>
                                        <div className={`nc-NcInputNumber flex items-center justify-between w-28`}>
                                            <button
                                                className="w-8 h-8 rounded-full flex items-center justify-center border border-neutral-400 dark:border-neutral-500 bg-white dark:bg-neutral-900 focus:outline-none hover:border-neutral-700 disabled:hover:border-neutral-400 dark:disabled:hover:border-neutral-500 disabled:opacity-50 disabled:cursor-default"
                                                type="button"
                                                aria-label={t('cart.decrease_quantity')}
                                                disabled={reachedMinQuantity}
                                                onClick={() => handleChangeCountButtonClick('decrease', sku, item)}
                                            >
                                                <MinusIcon className="w-4 h-4" />
                                            </button>
                                            <span>{sku.quantity || 0}</span>
                                            <button
                                                className="w-8 h-8 rounded-full flex items-center justify-center border border-neutral-400 dark:border-neutral-500 bg-white dark:bg-neutral-900 focus:outline-none hover:border-neutral-700 disabled:hover:border-neutral-400 dark:disabled:hover:border-neutral-500 disabled:opacity-50 disabled:cursor-default"
                                                type="button"
                                                aria-label={t('cart.increase_quantity')}
                                                disabled={reachedStockLimit}
                                                onClick={() => handleChangeCountButtonClick('increase', sku, item)}
                                            >
                                                <PlusIcon className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        {/* <SubtotalRender apiBreakdown={apiBreakdown} id={item.id} /> */}
                    </div>
                );
            })}
        </>
    );
};

export default CartSkuItems;
